import React, { useState } from 'react'
import {
    useTheme,
    Typography,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
} from '@mui/material'

interface NewMessageDialogProps {
    open: boolean
    onClose: () => void
    onSend: (subject: string, body: string) => Promise<void>
}

export const NewMessageDialog: React.FC<NewMessageDialogProps> = ({
    open,
    onClose,
    onSend,
}) => {
    const theme = useTheme()
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleClose = () => {
        if (isSubmitting) return
        setSubject('')
        setBody('')
        onClose()
    }

    const handleSend = async () => {
        if (!subject.trim() || !body.trim() || isSubmitting) return
        setIsSubmitting(true)
        try {
            await onSend(subject.trim(), body.trim())
            setSubject('')
            setBody('')
        } finally {
            setIsSubmitting(false)
        }
    }

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            color: 'rgba(255,255,255,0.8)',
            '& fieldset': {
                borderColor: 'rgba(255,255,255,0.2)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255,255,255,0.3)',
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(255,255,255,0.5)',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.primary.main,
        },
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    backgroundColor: '#2a2f35',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[700]}`,
                    maxWidth: '500px',
                    width: '90%',
                },
            }}
        >
            <DialogContent sx={{ padding: theme.spacing(3) }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: '#fff',
                        marginBottom: theme.spacing(2),
                        fontWeight: 600,
                    }}
                >
                    New Message
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: '#8b949e',
                        lineHeight: 1.6,
                        marginBottom: theme.spacing(2),
                    }}
                >
                    Start a new conversation with your apartment community.
                </Typography>
                <TextField
                    fullWidth
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="What is this about?"
                    disabled={isSubmitting}
                    sx={{ ...textFieldSx, marginBottom: theme.spacing(2) }}
                />
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Message"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Write your message..."
                    disabled={isSubmitting}
                    sx={textFieldSx}
                />
            </DialogContent>
            <DialogActions sx={{ padding: theme.spacing(3), paddingTop: 0 }}>
                <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    sx={{
                        color: '#8b949e',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: theme.spacing(1, 2),
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    disabled={
                        isSubmitting || !subject.trim() || !body.trim()
                    }
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: theme.spacing(1, 3),
                        borderRadius: 2,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        },
                    }}
                >
                    {isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        'Send'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
